import React, { useEffect, useMemo } from 'react'
import { pdfjs } from 'react-pdf'
import * as pdfJsWorker from 'pdfjs-dist/build/pdf.worker.js'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useViewStore } from '../Contexts/ViewContext'
import { useUtilsStore } from '../Contexts/UtilsContext'

import type { RetorikMainComponentConfiguration } from '../../models/retorikTypes'
import { ContainerParent, Mode, Routes } from '../../models/enums'
import getThemeColors from '../../utils/getThemeColors'
import { useRetorikUI } from '../CustomAdaptiveCard'

import ContextProvider from '../Contexts/ContextProvider'
import RetorikComposer from './RetorikComposer'
import RetorikRouter from './RetorikRouter'
import Container from './Container'
import NewsView from '../Views/NewsView'
import SpeechManager from '../Speech/SpeechManager'
import FocusManager from '../Speech/FocusManager'
import MobileLandscapePreventer from '../Utils/MobileLandscapePreventer'
import ErrorCollector from '../Telemetry/ErrorCollector'
import SendTypingIndicator from '../Utils/SendTypingIndicator'
import ChatbotContainer from '../ChatbotAndSubtitles/ChatbotAndSubtitlesContainer'

// Configure react-pdf worker
pdfjs.GlobalWorkerOptions.workerSrc = pdfJsWorker
// Overload adaptive card rendering classes
useRetorikUI()

interface RetorikContainerProps extends RetorikMainComponentConfiguration {
  parent: number
}

const RetorikContainer = ({
  config,
  viewsConfig,
  chatbotData,
  chatbotDataWidget,
  agentData,
  ponyfillFactoryCredentials,
  addressData,
  userData,
  skipLoader,
  defaultMode,
  customVoice,
  externalEventHandler,
  externalComponents,
  width,
  height,
  colors,
  parent
}: RetorikContainerProps): JSX.Element => {
  const currentLayout = useRetorikStore((state) => state.currentLayout)
  const mode = useSpeechStore((state) => state.mode)
  const processedAgentData = useRetorikStore((state) => state.agentData)
  const displayAgentInNews = useRetorikStore((state) => state.displayAgentInNews)
  const displayAgent = useUtilsStore((state) => state.displayAgent)
  const route = useViewStore((state) => state.route)
  const isMobile = useViewStore((state) => state.isMobile)
  const visibility = useViewStore((state) => state.visibility)
  const setThemeColors = useViewStore((state) => state.actions.setThemeColors)

  const isNews = useMemo<boolean>(() => {
    return parent === ContainerParent.news || route === Routes.News
  }, [parent, route])

  const isAgentVisible = useMemo<boolean>(() => {
    return displayAgent && !(isNews && !displayAgentInNews) && !(isMobile && mode === Mode.text) && (visibility === 'visible')
  }, [mode, isMobile, displayAgent, displayAgentInNews, isNews, visibility])

  useEffect(() => {
    setThemeColors(getThemeColors(colors))
  }, [])

  return config && viewsConfig && chatbotData && chatbotDataWidget && agentData && ponyfillFactoryCredentials ? (
    <ContextProvider
      skipLoader={skipLoader}
      mode={defaultMode || Mode.vocal}
      viewsConfiguration={viewsConfig}
      configuration={config}
      addressData={addressData}
      agentSource={agentData}
      ponyfillFactoryCredentials={ponyfillFactoryCredentials}
      customVoice={customVoice}
    >
      {!config.forceMobileView && <MobileLandscapePreventer />}
      <RetorikComposer addressData={addressData} userData={userData} externalEventHandler={externalEventHandler} isRetorikNews={parent === ContainerParent.news}>
        <ErrorCollector>
          <SpeechManager isRetorikNews={parent === ContainerParent.news} />
          <FocusManager />
          <Container fullSize={parent !== ContainerParent.widget && config.fullSize !== false} width={width} height={height} parent={parent}>
            {processedAgentData && (
              <ChatbotContainer
                centered={chatbotData.fullWidthInDesktopMode && !isNews}
                chatbotData={currentLayout === ContainerParent.agent ? chatbotData : chatbotDataWidget}
                showSubtitles={!isNews}
                visible={isAgentVisible}
              />
            )}
            {parent === ContainerParent.news ? <NewsView hideMenu={true} isRetorikNews={true} /> : <RetorikRouter externalComponents={externalComponents} />}
          </Container>
          <SendTypingIndicator isRetorikNews={parent === ContainerParent.news} />
        </ErrorCollector>
      </RetorikComposer>
    </ContextProvider>
  ) : (
    <React.Fragment />
  )
}

export default RetorikContainer
